import Swiper from 'swiper';

const CREATIVE_NAME = 'hero_slider';
const NAVIGATION_CREATIVE_NAME = 'hero_slider_navigation';

function pushPromotion(eventName, ecommerce) {
    window.dataLayer = window.dataLayer || [];
    // Clear the previous ecommerce object so values never carry over between events.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: eventName,
        ecommerce: ecommerce
    });
}

export default function HeroCarousel(el) {
    const swiperContainer = el.querySelector('.swiper-container');
    const navButtons = el.querySelectorAll('.hero-carousel__navigation button');
    const contentItems = el.querySelectorAll('.hero-carousel__content-item');
    const autoplayButton = el.querySelector('.hero-carousel__toggle-autoplay');
    const delay = el.getAttribute('data-carousel-delay') || 7000;
    let contentTimer = null;
    const viewedSlides = [];
    let isInView = false;

    function getSlideSlot(index) {
        return `slide_${index + 1}`;
    }

    function getSlideName(index) {
        const contentItem = contentItems[index];
        const title = contentItem ? contentItem.querySelector('h2') : null;

        return title ? title.textContent.trim() : '';
    }

    // One impression per slide and page view, and only while the carousel is on screen.
    function trackSlideView(index) {
        if (!isInView || viewedSlides.indexOf(index) !== -1) return;

        viewedSlides.push(index);

        pushPromotion('view_promotion', {
            creative_name: CREATIVE_NAME,
            creative_slot: getSlideSlot(index),
            promotion_name: getSlideName(index)
        });
    }

    function setAutoplayState(isPlaying) {
        const label = isPlaying ? autoplayButton.dataset.labelPause : autoplayButton.dataset.labelPlay;

        autoplayButton.classList.toggle('is-playing', isPlaying);
        autoplayButton.setAttribute('aria-label', label || (isPlaying ? 'Pause carousel' : 'Play carousel'));
    }

    setAutoplayState(true);

    const swiper = new Swiper(swiperContainer, {
        loop: true,
        slidesPerView: 1,
        autoplay: {
            delay: delay,
            disableOnInteraction: true
        },

        on: {
            slideChange: function () {
                const index = swiper.realIndex;
                updateContent(index);
                updateNavigation(index);
                trackSlideView(index);

                // Handle play/pause state if autoplay is not running after slideChange and drag event.
                if (!swiper.autoplay.running) {
                    setAutoplayState(false);
                }
            }
        }
    });

    function updateContent(nextIndex) {
        if (contentTimer) clearTimeout(contentTimer);

        const currentIndex = [...contentItems].findIndex((item) => item.classList.contains('active'));
        const currentItem = contentItems[currentIndex];
        const nextItem = contentItems[nextIndex];

        if (!nextItem) return;

        contentItems.forEach((item, i) => {
            item.classList.remove('fade-out');

            if (i === nextIndex) {
                item.removeAttribute('inert');
            } else {
                item.setAttribute('inert', '');
            }
        });

        if (currentItem && currentIndex !== nextIndex) {
            currentItem.classList.remove('active');

            requestAnimationFrame(() => {
                currentItem.classList.add('fade-out');
            });

            contentTimer = setTimeout(() => {
                currentItem.classList.remove('fade-out');
                nextItem.classList.add('active');
            }, 500);
        } else {
            contentItems.forEach((el, i) => {
                el.classList.toggle('active', i === nextIndex);
            });
        }
    }

    function updateNavigation(index) {
        const scrollEl = el.querySelector('.hero-carousel__navigation ul');

        navButtons.forEach((btn, i) => {
            const isActive = i === index;
            btn.classList.toggle('active', isActive);

            if (isActive && scrollEl.scrollWidth > scrollEl.clientWidth) {
                const item = btn.parentElement;
                let scrollLeft;

                if (i === navButtons.length - 1) {
                    scrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
                } else {
                    scrollLeft = item.offsetLeft;
                }

                scrollEl.scrollTo({
                    left: scrollLeft,
                    behavior: 'smooth'
                });
            }
        });
    }

    navButtons.forEach((button) => {
        button.addEventListener('mousedown', (e) => e.stopPropagation());
        button.addEventListener('touchstart', (e) => e.stopPropagation());
        button.addEventListener('click', () => {
            const index = parseInt(button.dataset.index, 10);

            pushPromotion('select_promotion', {
                creative_name: NAVIGATION_CREATIVE_NAME,
                creative_slot: getSlideSlot(index),
                promotion_name: getSlideName(index)
            });

            swiper.slideToLoop(index);
            swiper.autoplay.stop();
            setAutoplayState(false);
            updateNavigation(index);
        });
    });

    contentItems.forEach((contentItem, index) => {
        const cta = contentItem.querySelector('.hero-carousel__button');

        if (!cta) return;

        cta.addEventListener('click', () => {
            pushPromotion('select_promotion', {
                creative_name: CREATIVE_NAME,
                creative_slot: getSlideSlot(index),
                promotion_name: getSlideName(index),
                link_url: cta.getAttribute('href')
            });
        });
    });

    autoplayButton.addEventListener('click', () => {
        if (swiper.autoplay.running) {
            swiper.autoplay.stop();
            setAutoplayState(false);
        } else {
            swiper.autoplay.start();
            setAutoplayState(true);
        }
    });

    /* Observer Purpose
        - Pause auto play when scrolled beyond Image carousel
    */
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                isInView = entry.isIntersecting;

                if (entry.isIntersecting) {
                    trackSlideView(swiper.realIndex);
                } else {
                    swiper.autoplay.stop();
                    setAutoplayState(false);
                }
            });
        },
        {
            threshold: 0
        }
    );

    observer.observe(swiperContainer);

    // Init
    updateContent(swiper.realIndex);
    updateNavigation(swiper.realIndex);
}
